'use client';

/**
 * **`Other names` and `Other addresses`, as rows of the record** (VALE-863).
 *
 * These used to be two collapsed disclosures mounted *below* the attribute grid
 * under a hand-written `border-t pt-6` divider — the mechanical reason Tom's
 * 27 Aug review found them "floating at the bottom of the form". They also
 * carried their own chrome (a toggle, a bordered list, a grey inline card) and
 * their own type sizes, so even once moved they would still have read as a
 * widget parked on the form rather than part of it.
 *
 * They are now rows: a label in the shared label column, the variants stacked
 * in the value column, and an add affordance under them. Clicking a variant
 * opens the same compact composite popover the `Name` and `Address` fields open
 * ({@link ATTRIBUTE_POPOVER_PANEL}), which is what makes "edit a thing on this
 * record" one gesture everywhere on the surface instead of three.
 *
 * **Still not `AttributeSpec` rows, and that distinction stands.** A repeating
 * collection with its own add/remove lifecycle and per-row provenance is not a
 * label/value pair, and `components/attributes/CLAUDE.md` §1.9 is explicit that
 * editor weight scales with the shape of the value. What changed is only that
 * it stops *looking* like a different system: the geometry, the type and the
 * editor chrome are the grid's, read from the same tokens, so the rows line up
 * with the ones above them to the pixel.
 *
 * **They are `render` rows of the grid itself** ({@link AttributeSpec.render}),
 * not a block stacked beneath it. That is what lets each one sit *beside* the
 * scalar it belongs with — `Name` | `Other names` on one line, `Address` |
 * `Other addresses` on the next — which a separate block structurally cannot
 * do, since a block is its own div and cannot interleave with the grid's cells.
 *
 * So this module exports the **value slots** and the field specs that carry
 * them; the grid owns the label column, the placement and the motion, exactly
 * as it does for every other row.
 */

import { useCallback, useRef, useState } from 'react';
import { Check, Copy, Plus, X } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { sameDraft, type AttributeSpec } from '@/components/attributes';
import {
  ATTRIBUTE_AFFORDANCE,
  ATTRIBUTE_AFFORDANCE_REVEALED,
  ATTRIBUTE_EDITOR_BARE,
  ATTRIBUTE_INSET,
  ATTRIBUTE_LABEL_TEXT,
  ATTRIBUTE_LEADING,
  ATTRIBUTE_MUTED_INK,
  ATTRIBUTE_POPOVER_COLLISION_PADDING,
  ATTRIBUTE_POPOVER_PANEL,
  ATTRIBUTE_VALUE_TEXT
} from '@/components/attributes';
import {
  ADDRESS_VARIANT_TYPE_LABELS,
  formatVariantAddress,
  NAME_VARIANT_ROLE_LABELS,
  useCreateAddressVariant,
  useCreateNameVariant,
  useDeleteAddressVariant,
  useDeleteNameVariant,
  useIdentityVariants,
  useUpdateAddressVariant,
  useUpdateNameVariant,
  type AddressVariant,
  type CreateAddressVariantRequest,
  type CreateNameVariantRequest,
  type IdentityVariantOwnerType,
  type IdentityVariantsResponse,
  type NameVariant
} from '@/lib/services/identity-variants';
import { cn } from '@/lib/utils';
import { AddressVariantForm } from './address-variant-form';
import { NameVariantForm } from './name-variant-form';
import { isPersistedId } from '@/lib/utils/persisted-record';

/**
 * One variant, at rest: its value, and the qualifiers that make two otherwise
 * identical-looking rows distinguishable (a maiden name against a spelling
 * variant). The meta reads as a subordinate of the value rather than a second
 * value, so it takes the label rung's size and ink.
 */
function VariantReadOut({ value, meta }: { value: string; meta: string }) {
  return (
    <span className="flex min-w-0 items-baseline gap-2">
      <span className="truncate">{value}</span>
      {/*
        `truncate` is load-bearing, not decoration. Without it this span carries
        no wrapping or shrink control at all, so as a flex item in a `min-w-0`
        row it shrinks below its content width and wraps word-by-word into a
        tall narrow column beside the value. An address variant's meta ends in
        `periodNote` — free text the user typed — so it is arbitrarily long and
        this is reachable with ordinary input.
      */}
      {meta && (
        <span
          className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK, 'truncate')}
        >
          {meta}
        </span>
      )}
    </span>
  );
}

/**
 * The trigger a variant and the add affordance share.
 *
 * Bare, inset and on the value type — the same three tokens an
 * `AttributeReadOut` wears — so a variant is the same target as any other value
 * on the surface and the popover opens from the same place the text sits.
 */
function VariantTrigger({
  label,
  children,
  muted = false
}: {
  label: string;
  children: React.ReactNode;
  muted?: boolean;
}) {
  return (
    <PopoverTrigger asChild>
      <button
        type="button"
        aria-label={label}
        className={cn(
          ATTRIBUTE_VALUE_TEXT,
          ATTRIBUTE_INSET,
          ATTRIBUTE_LEADING,
          ATTRIBUTE_EDITOR_BARE,
          'hover:bg-surface-subtle flex min-w-0 items-center rounded-md text-left transition-colors',
          muted ? ATTRIBUTE_MUTED_INK : 'text-foreground'
        )}
      >
        {children}
      </button>
    </PopoverTrigger>
  );
}

/**
 * One row — a label, its variants, and the add affordance beneath them.
 *
 * Generic over the variant type so the two kinds share the row rather than the
 * row being written twice and drifting, which is the shape the two subsections
 * this replaces had.
 */
function VariantValues<TVariant extends { id: number }>({
  addLabel,
  entityLabel,
  variants,
  isLoading,
  copyTextOf,
  renderReadOut,
  renderForm,
  onDelete,
  openId,
  onOpenChange,
  onRevert
}: {
  addLabel: string;
  entityLabel: string;
  variants: TVariant[];
  isLoading: boolean;
  /** What the copy affordance puts on the clipboard for this variant. */
  copyTextOf: (variant: TVariant) => string;
  renderReadOut: (variant: TVariant) => React.ReactNode;
  renderForm: (variant: TVariant | 'new', close: () => void) => React.ReactNode;
  onDelete: (variant: TVariant) => Promise<void>;
  openId: number | 'new' | null;
  onOpenChange: (next: number | 'new' | null) => void;
  /**
   * Drop the draft without committing it. Wired to `onEscapeKeyDown` below,
   * because Radix's escape dismissal does not go through the form's own
   * `onCancel` — see {@link revertName}.
   */
  onRevert: () => void;
}) {
  if (isLoading) return <Skeleton className="h-4 w-32" />;

  return (
    <>
      {variants.map((variant) => (
        <Popover
          key={variant.id}
          open={openId === variant.id}
          onOpenChange={(open) => onOpenChange(open ? variant.id : null)}
        >
          {/*
            `group/cell` is what the affordances reveal against
            ({@link ATTRIBUTE_AFFORDANCE_REVEALED}). A normal row gets it from
            {@link ATTRIBUTE_CELL}; a `render` row has to establish its own, and
            per variant rather than per row — otherwise hovering anywhere in the
            column would light up every variant's buttons at once.
          */}
          <div className="group/cell flex min-w-0 items-center gap-1 pr-1">
            <VariantTrigger label={`Edit this ${entityLabel}`}>
              {renderReadOut(variant)}
            </VariantTrigger>
            <VariantAffordances
              entityLabel={entityLabel}
              copyText={copyTextOf(variant)}
              onDelete={() => onDelete(variant)}
            />
          </div>
          <PopoverContent
            align="start"
            collisionPadding={ATTRIBUTE_POPOVER_COLLISION_PADDING}
            className={ATTRIBUTE_POPOVER_PANEL}
            onEscapeKeyDown={(event) => {
              /*
                **Radix dismisses on `Escape` without going through the form's
                `onCancel`**, so without this the draft refs were still
                populated when `onOpenChange(null)` reached `commitName` and the
                revert saved instead (VALE-1710; pre-existing, measured on
                develop). Preventing the default stops the host drawer seeing
                the key as well, which is the same reason
                `useAttributePopoverCommit` does it for the other eight panels.
              */
              event.preventDefault();
              onRevert();
              onOpenChange(null);
            }}
          >
            {renderForm(variant, () => onOpenChange(null))}
          </PopoverContent>
        </Popover>
      ))}

      <Popover
        open={openId === 'new'}
        onOpenChange={(open) => onOpenChange(open ? 'new' : null)}
      >
        <VariantTrigger label={addLabel} muted>
          <Plus className="mr-1 h-3 w-3 shrink-0" aria-hidden="true" />
          {addLabel}
        </VariantTrigger>
        <PopoverContent
          align="start"
          collisionPadding={ATTRIBUTE_POPOVER_COLLISION_PADDING}
          className={ATTRIBUTE_POPOVER_PANEL}
          onEscapeKeyDown={(event) => {
            // See the note on the per-variant popover above.
            event.preventDefault();
            onRevert();
            onOpenChange(null);
          }}
        >
          {renderForm('new', () => onOpenChange(null))}
        </PopoverContent>
      </Popover>
    </>
  );
}

/**
 * Copy and clear, on a variant.
 *
 * The same two gestures a normal row offers, drawn from the same tokens — a
 * `render` row draws its own value slot, so it does not inherit the cell's
 * affordances and has to earn them back rather than go without.
 *
 * **"Clear" removes the variant**, which is what clearing one of a set means:
 * a normal row clears to `clearTo` because it still exists afterwards with an
 * empty value, and a variant with no value is not a record of anything.
 */
function VariantAffordances({
  entityLabel,
  copyText,
  onDelete
}: {
  entityLabel: string;
  copyText: string;
  onDelete: () => void | Promise<void>;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          void navigator.clipboard?.writeText(copyText).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1200);
          });
        }}
        title={copied ? 'Copied' : `Copy this ${entityLabel}`}
        /*
          The accessible name carries the state, not just the glyph: a copy is
          the one gesture here with no observable result, so the tick *is* the
          feedback — the same reasoning `AttributeValueCell` records.
        */
        aria-label={
          copied ? `Copied ${entityLabel}` : `Copy this ${entityLabel}`
        }
        className={cn(ATTRIBUTE_AFFORDANCE, ATTRIBUTE_AFFORDANCE_REVEALED)}
      >
        {copied ? (
          <Check className="text-success-subtle-foreground h-3.5 w-3.5 shrink-0" />
        ) : (
          <Copy className="h-3.5 w-3.5 shrink-0" />
        )}
      </button>
      <button
        type="button"
        /*
          `onClick`, not `onMouseDown`, and `stopPropagation`: the trigger beside
          it opens the editor, and removing a variant must not also leave an
          editor open over the thing it just removed.
        */
        onClick={(event) => {
          event.stopPropagation();
          void onDelete();
        }}
        title={`Remove this ${entityLabel}`}
        aria-label={`Remove this ${entityLabel}`}
        className={cn(ATTRIBUTE_AFFORDANCE, ATTRIBUTE_AFFORDANCE_REVEALED)}
      >
        <X className="h-3.5 w-3.5 shrink-0" />
      </button>
    </>
  );
}

export interface UseIdentityVariantFieldsOptions {
  ownerType: IdentityVariantOwnerType;
  /** Undefined while the owner is unsaved — the rows render a hint instead. */
  ownerId: number | undefined;
  /** Storybook / test seam — bypasses the query so the rows render given data. */
  data?: IdentityVariantsResponse;
}

/**
 * **The two variant rows, as field specs the caller drops into its grid.**
 *
 * A hook rather than a component because the rows have to be *fields* — that is
 * the only way `Other names` lands in the grid cell beside `Name` instead of in
 * a block below the whole grid. The caller decides where in its field list they
 * sit; this owns the data, the popovers and the open state.
 */
export function useIdentityVariantFields({
  ownerType,
  ownerId,
  data: dataOverride
}: UseIdentityVariantFieldsOptions): AttributeSpec<never>[] {
  const owner = { ownerType, ownerId };
  const [openName, setOpenName] = useState<number | 'new' | null>(null);
  const [openAddress, setOpenAddress] = useState<number | 'new' | null>(null);

  /*
    **Commit-on-dismiss** (VALE-863) — the contract every other editor on this
    treatment keeps: dismissing saves, `Escape` reverts. The form reports its
    payload here on every change and the host sends it when the popover closes,
    because a dismissal can come from a click anywhere on the page and there is
    no blur guaranteed to land first.

    **A revert clears the draft; it does not raise a flag.** Radix fires
    `onOpenChange(false)` *after* `onEscapeKeyDown`, so the commit-on-dismiss
    that follows a revert has to be stopped somehow — and this used to be a
    shared `revertRef` boolean set in `onCancel` just before `close()`.

    That latched. The flag was armed *outside* the handler that cleared it, so
    any `Escape` whose `close()` did not reach `handleNameOpen(null)` left it
    armed for the whole surface — and the **next** dismissal of **any** name row
    then took the revert branch and silently discarded a real edit. One sticky
    boolean shared by every row, and the symptom was "Escape doesn't work, and
    now the next popover won't save".

    Clearing `draftRef` instead is self-enforcing: `commitName` returns early on
    a null payload, so the dismissal cannot write, and there is no state left
    behind to leak into the next open. The reverting path now proves itself
    rather than asking the next one to remember.
  */
  const nameDraftRef = useRef<CreateNameVariantRequest | null>(null);
  const addressDraftRef = useRef<CreateAddressVariantRequest | null>(null);
  /*
    What each popover opened with, so a dismissal that changed nothing writes
    nothing. `sameDraft` is the treatment's own comparison — a composite draft
    is rebuilt per keystroke, so reference equality would call every dismissal
    dirty. Without this an untouched popover still sent a PATCH, which is how an
    empty `residential` address reached the list.
  */
  const nameOpenedWithRef = useRef<CreateNameVariantRequest | null>(null);
  const addressOpenedWithRef = useRef<CreateAddressVariantRequest | null>(null);
  /*
    Explicit, rather than inferred from the refs being null: a *new* variant's
    initial payload IS null, so "have I recorded the baseline yet?" cannot be
    answered by looking at it — the first keystroke would be mistaken for the
    baseline and the row would never save.
  */
  const nameAwaitingBaselineRef = useRef(false);
  const addressAwaitingBaselineRef = useRef(false);

  /*
    Stable, so the forms' draft-reporting effect fires on a real change rather
    than on every render — both only ever write a ref.
  */
  const reportNameDraft = useCallback(
    (payload: CreateNameVariantRequest | null) => {
      // The first report after an open is the form's initial state — that is
      // what "unchanged" is measured against.
      if (nameAwaitingBaselineRef.current) {
        nameOpenedWithRef.current = payload;
        nameAwaitingBaselineRef.current = false;
      }
      nameDraftRef.current = payload;
    },
    []
  );
  const reportAddressDraft = useCallback(
    (payload: CreateAddressVariantRequest | null) => {
      if (addressAwaitingBaselineRef.current) {
        addressOpenedWithRef.current = payload;
        addressAwaitingBaselineRef.current = false;
      }
      addressDraftRef.current = payload;
    },
    []
  );

  /*
    `isPersistedId`, not `!!ownerId`. A pending record's synthetic id is
    NEGATIVE — and therefore truthy — so a presence check fetches this
    collection for a contact that does not exist yet (VALE-977). These rows
    moved here from the contact drawer in VALE-863, where the drawer's own
    `isPersistedRecord` guard was what stopped it.
  */
  const query = useIdentityVariants(owner, {
    enabled: !dataOverride && isPersistedId(ownerId)
  });
  const data = dataOverride ?? query.data;

  const createName = useCreateNameVariant(owner, { silentSuccess: true });
  const updateName = useUpdateNameVariant(owner, { silentSuccess: true });
  const removeName = useDeleteNameVariant(owner, { silentSuccess: true });
  const createAddress = useCreateAddressVariant(owner, { silentSuccess: true });
  const updateAddress = useUpdateAddressVariant(owner, { silentSuccess: true });
  const removeAddress = useDeleteAddressVariant(owner, { silentSuccess: true });

  /*
    Primary rows are excluded, unchanged from the subsection this replaces: a
    primary name variant *is* the canonical name, already rendered by the `Name`
    row directly above, so listing it here would duplicate that row and
    contradict the heading.
  */
  const nameVariants = (data?.nameVariants ?? []).filter(
    (variant) => !variant.isPrimary
  );
  const addressVariants = data?.addressVariants ?? [];

  const isLoading = query.isLoading && !dataOverride;

  /*
    No owner yet means no variants can exist: they attach to a contact id the
    add flow has not minted (LF-5679). The rows are omitted rather than rendered
    empty — `hidden` is the grid's own idiom for a row that does not apply, and
    an "add" affordance that cannot save is worse than no row.
  */
  const commitName = (closing: number | 'new') => {
    const payload = nameDraftRef.current;
    const openedWith = nameOpenedWithRef.current;
    nameDraftRef.current = null;
    nameOpenedWithRef.current = null;
    nameAwaitingBaselineRef.current = false;
    if (!payload) return;
    if (sameDraft(payload, openedWith)) return;
    if (closing === 'new') {
      void createName.mutateAsync(payload);
    } else {
      void updateName.mutateAsync({ id: closing, data: payload });
    }
  };

  const commitAddress = (closing: number | 'new') => {
    const payload = addressDraftRef.current;
    const openedWith = addressOpenedWithRef.current;
    addressDraftRef.current = null;
    addressOpenedWithRef.current = null;
    addressAwaitingBaselineRef.current = false;
    if (!payload) return;
    if (sameDraft(payload, openedWith)) return;
    if (closing === 'new') {
      void createAddress.mutateAsync(payload);
    } else {
      void updateAddress.mutateAsync({ id: closing, data: payload });
    }
  };

  /**
   * **Drop the draft so the dismiss that follows commits nothing** — the revert
   * half of the same pair {@link commitName} is the commit half of.
   *
   * Extracted at VALE-1710 because `Escape` needed it too. It was inline in the
   * form's `onCancel`, which Radix's own dismissal races: the popover's
   * document-level escape listener fires `onOpenChange(null)` independently of
   * the React handler, so `commitName` ran with the refs still populated and
   * **`Escape` saved the edit it is supposed to discard**. Measured on
   * `origin/develop`, so this is a pre-existing defect rather than one the
   * confirm control introduced; it is fixed here because this is the file, and
   * a silent wrong-save is not a thing to leave beside a new Done button.
   */
  const revertName = () => {
    nameDraftRef.current = null;
    nameOpenedWithRef.current = null;
    nameAwaitingBaselineRef.current = false;
  };

  const revertAddress = () => {
    addressDraftRef.current = null;
    addressOpenedWithRef.current = null;
    addressAwaitingBaselineRef.current = false;
  };

  const handleNameOpen = (next: number | 'new' | null) => {
    if (next !== null) nameAwaitingBaselineRef.current = true;
    // A reverted popover has already cleared its draft, so this is a no-op for
    // it — see the note on the draft refs above for why that beats a flag.
    if (next === null && openName !== null) commitName(openName);
    setOpenName(next);
  };

  const handleAddressOpen = (next: number | 'new' | null) => {
    if (next !== null) addressAwaitingBaselineRef.current = true;
    if (next === null && openAddress !== null) commitAddress(openAddress);
    setOpenAddress(next);
  };

  /*
    The same predicate the query above is gated on, for the same reason: a
    pending record's synthetic id is negative and therefore truthy, so `!ownerId`
    rendered a live `Add name` affordance over a contact the AI-extract review
    had not created yet, and its POST went to `/contacts/-1/…` (VALE-1505).
  */
  const hidden = !isPersistedId(ownerId);

  return [
    {
      label: 'Other names',
      display: null,
      draft: '',
      editor: { kind: 'text' },
      hidden,
      toPatch: () => null,
      render: () => (
        <VariantValues<NameVariant>
          addLabel="Add name"
          entityLabel="name"
          variants={nameVariants}
          isLoading={isLoading}
          copyTextOf={(variant) => variant.value}
          onDelete={async (variant) => {
            await removeName.mutateAsync(variant.id);
          }}
          openId={openName}
          onOpenChange={handleNameOpen}
          onRevert={revertName}
          renderReadOut={(variant) => (
            <VariantReadOut
              value={variant.value}
              meta={[
                NAME_VARIANT_ROLE_LABELS[variant.role],
                !variant.isCurrent && 'previous'
              ]
                .filter(Boolean)
                .join(' \u00b7 ')}
            />
          )}
          renderForm={(variant, close) => (
            <NameVariantForm
              bare
              variant={variant === 'new' ? undefined : variant}
              isSubmitting={
                variant === 'new' ? createName.isPending : updateName.isPending
              }
              /*
                Cancel and `Escape` both land here, and both must revert rather
                than save — the flag is what stops the dismiss that follows from
                committing the draft being abandoned.
              */
              onCancel={() => {
                revertName();
                close();
              }}
              autoSave
              onDraftChange={reportNameDraft}
              /*
                **Does not close — the form does, and the order is the whole
                point** (VALE-1710).

                `NameVariantForm.handleSubmit` awaits this and then calls
                `onCancel`, which drops the draft refs BEFORE closing, so the
                dismiss that follows has nothing left to commit. Closing here
                instead fired that dismiss while the refs were still populated,
                and `commitName` wrote a second time: two POSTs on an add (two
                real duplicate rows) and two PATCHes on an edit.

                Pre-existing on the `Enter` path since VALE-863 and measured
                here; VALE-1710's visible control is what made it reachable by
                mouse, which is how it was finally seen.
              */
              onSubmit={async (payload) => {
                if (variant === 'new') {
                  await createName.mutateAsync(payload);
                } else {
                  await updateName.mutateAsync({
                    id: variant.id,
                    data: payload
                  });
                }
              }}
            />
          )}
        />
      )
    },
    {
      label: 'Other addresses',
      display: null,
      draft: '',
      editor: { kind: 'text' },
      hidden,
      toPatch: () => null,
      render: () => (
        <VariantValues<AddressVariant>
          addLabel="Add address"
          entityLabel="address"
          variants={addressVariants}
          isLoading={isLoading}
          copyTextOf={(variant) => formatVariantAddress(variant.value)}
          onDelete={async (variant) => {
            await removeAddress.mutateAsync(variant.id);
          }}
          openId={openAddress}
          onOpenChange={handleAddressOpen}
          onRevert={revertAddress}
          renderReadOut={(variant) => (
            <VariantReadOut
              value={formatVariantAddress(variant.value)}
              meta={[
                ADDRESS_VARIANT_TYPE_LABELS[variant.type],
                !variant.isCurrent && 'previous',
                variant.periodNote
              ]
                .filter(Boolean)
                .join(' \u00b7 ')}
            />
          )}
          renderForm={(variant, close) => (
            <AddressVariantForm
              bare
              variant={variant === 'new' ? undefined : variant}
              isSubmitting={
                variant === 'new'
                  ? createAddress.isPending
                  : updateAddress.isPending
              }
              onCancel={() => {
                revertAddress();
                close();
              }}
              autoSave
              onDraftChange={reportAddressDraft}
              // Does not close — see the `Other names` note above; the form's
              // own `onCancel` drops the draft first, which is what stops the
              // dismiss committing a second time.
              onSubmit={async (payload) => {
                if (variant === 'new') {
                  await createAddress.mutateAsync(payload);
                } else {
                  await updateAddress.mutateAsync({
                    id: variant.id,
                    data: payload
                  });
                }
              }}
            />
          )}
        />
      )
    }
  ];
}
